import axios from "axios";
import api from "services/api";
import type {
  ContaFinanceiraFormPayload,
  ContaFinanceiraGerencialApi,
  ConciliacaoSaldoEfiApi,
  ContasFinanceirasGerencialApi,
  TipoContaFinanceiraApi,
} from "../types/contas-financeiras.types";

function erroAxios(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined;
    if (data?.error && typeof data.error === "string") return data.error;
    return e.message || "Falha na requisição.";
  }
  return e instanceof Error ? e.message : "Falha na requisição.";
}

export async function listarContasFinanceirasGerencial(): Promise<{
  data: ContasFinanceirasGerencialApi | null;
  status: number;
  error?: string;
}> {
  try {
    const res = await api.get<ContasFinanceirasGerencialApi>(
      "/loja/financeiro/contas-financeiras/gerencial",
    );
    return { data: res.data ?? null, status: res.status };
  } catch (e: unknown) {
    return {
      data: null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: erroAxios(e),
    };
  }
}

function parseSaldo(raw: string): number {
  const t = raw.trim().replace(/\s/g, "");
  if (!t) return 0;
  const n = Number(t.replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function montarPayloadContaFinanceira(
  form: ContaFinanceiraFormPayload,
): {
  nome: string;
  tipo: TipoContaFinanceiraApi;
  saldoInicial: number;
  ehPadrao: boolean;
  ehContaRepasse: boolean;
} {
  const repasse = form.tipo === "GATEWAY" ? false : form.ehContaRepasse;
  return {
    nome: form.nome.trim(),
    tipo: form.tipo,
    saldoInicial: parseSaldo(form.saldoInicial),
    ehPadrao: form.ehPadrao,
    ehContaRepasse: repasse,
  };
}

export async function criarContaFinanceiraApi(payload: {
  nome: string;
  tipo: TipoContaFinanceiraApi;
  saldoInicial?: number;
  ehPadrao?: boolean;
  ehContaRepasse?: boolean;
}): Promise<{
  data: ContaFinanceiraGerencialApi | null;
  status: number;
  error?: string;
}> {
  try {
    const res = await api.post("/loja/financeiro/contas-financeiras", payload);
    return { data: res.data as ContaFinanceiraGerencialApi, status: res.status };
  } catch (e: unknown) {
    return {
      data: null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: erroAxios(e),
    };
  }
}

export async function atualizarContaFinanceiraApi(
  id: string,
  payload: Partial<{
    nome: string;
    tipo: TipoContaFinanceiraApi;
    saldoInicial: number;
    ehPadrao: boolean;
    ehContaRepasse: boolean;
    ativo: boolean;
  }>,
): Promise<{ status: number; error?: string }> {
  try {
    const res = await api.patch(
      `/loja/financeiro/contas-financeiras/${encodeURIComponent(id)}`,
      payload,
    );
    return { status: res.status };
  } catch (e: unknown) {
    return {
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: erroAxios(e),
    };
  }
}

export async function excluirContaFinanceiraApi(
  id: string,
): Promise<{ status: number; error?: string }> {
  try {
    const res = await api.delete(
      `/loja/financeiro/contas-financeiras/${encodeURIComponent(id)}`,
    );
    return { status: res.status };
  } catch (e: unknown) {
    return {
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: erroAxios(e),
    };
  }
}

export async function transferirEntreContasFinanceirasApi(payload: {
  contaOrigemId: string;
  contaDestinoId: string;
  valor: number;
  observacao?: string;
}): Promise<{
  data: { movimentacaoId: string } | null;
  status: number;
  error?: string;
}> {
  try {
    const res = await api.post<{ movimentacaoId: string }>(
      "/loja/financeiro/contas-financeiras/transferencia",
      payload,
    );
    return { data: res.data ?? null, status: res.status };
  } catch (e: unknown) {
    return {
      data: null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: erroAxios(e),
    };
  }
}

export async function conciliarSaldoGatewayEfiApi(): Promise<{
  data: ConciliacaoSaldoEfiApi | null;
  status: number;
  error?: string;
}> {
  try {
    const res = await api.post<ConciliacaoSaldoEfiApi>(
      "/loja/financeiro/contas-financeiras/conciliar-efi",
    );
    return { data: res.data ?? null, status: res.status };
  } catch (e: unknown) {
    const ax = e as {
      response?: { status?: number; data?: ConciliacaoSaldoEfiApi | { error?: string } };
    };
    const body = ax.response?.data;
    const motivo =
      body && typeof body === "object" && "motivo" in body && body.motivo
        ? String(body.motivo)
        : body && typeof body === "object" && "error" in body
          ? String(body.error)
          : erroAxios(e);
    return {
      data: body && typeof body === "object" && "aplicada" in body
        ? (body as ConciliacaoSaldoEfiApi)
        : null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: motivo,
    };
  }
}

export async function conciliarSaldoContaApi(
  contaId: string,
  payload: { saldoReal: number; observacao?: string },
): Promise<{
  data: ConciliacaoSaldoEfiApi | null;
  status: number;
  error?: string;
}> {
  try {
    const res = await api.post<ConciliacaoSaldoEfiApi>(
      `/loja/financeiro/contas-financeiras/${encodeURIComponent(contaId)}/conciliar-saldo`,
      payload,
    );
    return { data: res.data ?? null, status: res.status };
  } catch (e: unknown) {
    const ax = e as {
      response?: { status?: number; data?: ConciliacaoSaldoEfiApi | { error?: string } };
    };
    const body = ax.response?.data;
    const motivo =
      body && typeof body === "object" && "motivo" in body && body.motivo
        ? String(body.motivo)
        : body && typeof body === "object" && "error" in body
          ? String(body.error)
          : erroAxios(e);
    return {
      data: body && typeof body === "object" && "aplicada" in body
        ? (body as ConciliacaoSaldoEfiApi)
        : null,
      status: axios.isAxiosError(e) ? e.response?.status ?? 0 : 0,
      error: motivo,
    };
  }
}
